/**
 * The composer's attachments (owner 2026-11-01: "allow file upload. most uploads will be prewritten specs + images"):
 * an assistant-ui `AttachmentAdapter` over the uploads API, as erp's attachments upload
 * (`/Users/ivan/Work/erp/web/src/pages/clients/components/Attachments.js`): presign → PUT the file straight to object
 * storage → finalize.
 *
 * Each file uploads as soon as it is picked, dropped or pasted, so sending waits only for what is still on its way. A
 * file the server refuses (its type, its size, a PDF without text) stays as a chip with the reason, and the message
 * cannot go until it is removed; a file removed on its way stops, and leaves nothing on the server. A message carries
 * the ready files (`attachmentOf`) by upload id, never the bytes.
 *
 * Nothing is left behind (review W-27, 2026-11-01): an upload that fails on its way (the PUT, the server refusing it at
 * finalize) or that the composer drops (removed, or the conversation closed: `dispose`) is deleted on the server at
 * once, not left to the day-old sweep. A composer holds at most `MAX_ATTACHMENTS_PER_MESSAGE` files, which also bounds
 * the PUTs running at once; a PUT that sends nothing for a minute stops.
 */
import type { Attachment, AttachmentAdapter, CompleteAttachment, PendingAttachment } from '@assistant-ui/react';
import {
  ATTACHABLE_FILES,
  MAX_ATTACHMENTS_PER_MESSAGE,
  type UploadRule,
  UPLOAD_ACCEPT,
  mediaTypeForFile,
  uploadRuleFor,
} from '@confluent/shared/lib/upload-rules';
import { deleteUpload, finalizeUpload, presignUpload } from '../../generated/api/sdk.gen.js';
import type { MessageAttachment, Upload } from '../../generated/api/types.gen.js';

const MB = 1024 * 1024;
/** A PUT that sends no byte for this long has stalled (a dead connection): it stops and says so. */
export const PUT_STALL_MS = 60_000;
/** The whole PUT, its answer included: the presigned URL's own lifetime (the server signs it for 15 minutes). */
const PUT_TIMEOUT_MS = 15 * 60_000;

export interface UploadAttachments extends AttachmentAdapter {
  /** The file a composer attachment became, as a message carries it; undefined until it is ready. */
  attachmentOf(attachmentId: string): MessageAttachment | undefined;
  /** The composer is gone: stop every upload still in it and delete what it left on the server. */
  dispose(): void;
}

/** The calls an upload makes; the defaults are the generated SDK and the browser's PUT. */
export interface UploadTransport {
  presign(file: {
    fileName: string;
    mediaType: string;
    sizeBytes: number;
  }): Promise<{ uploadId: number; uploadUrl: string }>;
  put(
    url: string,
    file: File,
    mediaType: string,
    onProgress: (fraction: number) => void,
    signal: AbortSignal,
  ): Promise<void>;
  /** The upload, ready: the server checked the stored object and, for a document, kept its text. */
  finalize(uploadId: number): Promise<Upload>;
  remove(uploadId: number): Promise<void>;
}

/**
 * The PUT to object storage on its presigned URL. It goes to the storage host, not our API, so the generated SDK
 * cannot make it; `XMLHttpRequest` because it reports upload progress (`fetch` does not). Aborting the signal stops it;
 * so does a minute without a byte sent, and the URL's lifetime.
 */
function putToStorage(
  url: string,
  file: File,
  mediaType: string,
  onProgress: (fraction: number) => void,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal.aborted) {
      reject(signal.reason);
      return;
    }
    const request = new XMLHttpRequest();
    const abort = () => request.abort();
    signal.addEventListener('abort', abort, { once: true });
    let stalled = false;
    let stall: ReturnType<typeof setTimeout> | undefined;
    const watchStall = () => {
      clearTimeout(stall);
      stall = setTimeout(() => {
        stalled = true;
        request.abort();
      }, PUT_STALL_MS);
    };
    const settle = (outcome: () => void) => {
      clearTimeout(stall);
      signal.removeEventListener('abort', abort);
      outcome();
    };
    request.open('PUT', url);
    request.timeout = PUT_TIMEOUT_MS;
    request.setRequestHeader('Content-Type', mediaType);
    // Signed into the URL (review N-12): storage takes the PUT only when the file is not stored yet, so the URL can never
    // replace a file finalize has checked. Without the header the signature does not match (403).
    request.setRequestHeader('If-None-Match', '*');
    request.upload.onprogress = (event) => {
      watchStall();
      if (event.lengthComputable) onProgress(event.loaded / event.total);
    };
    // The whole file is sent: what is left is the storage's answer, bounded by the request's own timeout.
    request.upload.onload = () => clearTimeout(stall);
    request.onload = () =>
      settle(() =>
        request.status >= 200 && request.status < 300
          ? resolve()
          : reject(new Error(`The file could not be stored (${request.status}). Attach it again.`)),
      );
    request.onerror = () =>
      settle(() => reject(new Error('The file could not be uploaded. Check your connection and attach it again.')));
    request.ontimeout = () =>
      settle(() => reject(new Error('The upload took too long. Check your connection and attach it again.')));
    request.onabort = () =>
      settle(() =>
        reject(stalled ? new Error('The upload stalled. Check your connection and attach it again.') : signal.reason),
      );
    watchStall();
    request.send(file);
  });
}

export const defaultUploadTransport: UploadTransport = {
  async presign(file) {
    const { data } = await presignUpload({ body: file, throwOnError: true });
    return { uploadId: data.uploadId, uploadUrl: data.uploadUrl };
  },
  put: putToStorage,
  async finalize(uploadId) {
    const { data } = await finalizeUpload({ path: { id: uploadId }, throwOnError: true });
    return data;
  },
  async remove(uploadId) {
    await deleteUpload({ path: { id: uploadId }, throwOnError: true });
  },
};

/** Why a picked file cannot be attached, before anything is uploaded (the server checks the same rules again). */
export function refusalOf(file: File, mediaType: string | null, rule: UploadRule | null): string | null {
  if (!mediaType || !rule) return ATTACHABLE_FILES;
  if (file.size === 0) return 'This file is empty.';
  if (file.size > rule.maxBytes) return `A ${rule.label} can be up to ${Math.round(rule.maxBytes / MB)} MB.`;
  return null;
}

const errorMessage = (error: unknown) => (error instanceof Error ? error.message : 'The file could not be attached.');

/** A ready upload as a message carries it. */
const asAttachment = (upload: Upload): MessageAttachment => ({
  uploadId: upload.id,
  kind: upload.kind,
  name: upload.name,
  mediaType: upload.mediaType,
  sizeBytes: upload.sizeBytes,
});

export function createUploadAttachments(transport: UploadTransport = defaultUploadTransport): UploadAttachments {
  /** The uploads on their way, by composer attachment: what a send waits for, and what removing one stops. */
  const inFlight = new Map<string, { done: Promise<MessageAttachment>; controller: AbortController }>();
  const ready = new Map<string, MessageAttachment>();
  /** The files in the composer, uploading or ready, not yet handed to a message: what the cap counts. */
  const composing = new Set<string>();
  /** Delete an upload the composer no longer holds; a delete that fails leaves it to the server's sweep. */
  const discard = (uploadId: number) => transport.remove(uploadId).catch(() => undefined);

  return {
    // The extensions as well as the types: a browser often names no type for Markdown.
    accept: UPLOAD_ACCEPT.join(','),

    attachmentOf: (attachmentId) => ready.get(attachmentId),

    async *add({ file }) {
      const id = `local-${crypto.randomUUID()}`;
      const mediaType = mediaTypeForFile(file.name, file.type);
      const rule = mediaType ? uploadRuleFor(mediaType) : null;
      const base = { id, type: rule?.kind ?? 'file', name: file.name, contentType: mediaType ?? file.type, file };
      const state = (status: PendingAttachment['status']): PendingAttachment => ({ ...base, status });
      // A chip at once, then the reason it cannot go (thrown: the composer marks it failed with the message).
      yield state({ type: 'running', reason: 'uploading', progress: 0 });
      const refusal = refusalOf(file, mediaType, rule);
      if (refusal || !mediaType) throw new Error(refusal ?? 'This file cannot be attached.');
      if (composing.size >= MAX_ATTACHMENTS_PER_MESSAGE) {
        throw new Error(`A message can carry up to ${MAX_ATTACHMENTS_PER_MESSAGE} files.`);
      }
      composing.add(id);

      const controller = new AbortController();
      let progress = 0;
      let wake: () => void = () => {};
      const upload = async (): Promise<MessageAttachment> => {
        const { uploadId, uploadUrl } = await transport.presign({
          fileName: file.name,
          mediaType,
          sizeBytes: file.size,
        });
        try {
          const onProgress = (fraction: number) => {
            progress = fraction;
            wake();
          };
          await transport.put(uploadUrl, file, mediaType, onProgress, controller.signal);
          const finalized = await transport.finalize(uploadId);
          controller.signal.throwIfAborted();
          return asAttachment(finalized);
        } catch (error) {
          // Removed on its way, failed on the PUT or refused at finalize: what it left on the server goes with it.
          await discard(uploadId);
          throw error;
        }
      };
      const done = upload();
      inFlight.set(id, { done, controller });
      const finished = done.then(
        () => true,
        () => true,
      );
      try {
        // Each progress the PUT reports is shown as it comes; nothing polls.
        let shown = 0;
        while (true) {
          if (progress !== shown) {
            shown = progress;
            yield state({ type: 'running', reason: 'uploading', progress });
            continue;
          }
          const changed = new Promise<boolean>((resolve) => {
            wake = () => resolve(false);
          });
          if (await Promise.race([changed, finished])) break;
        }
        ready.set(id, await done);
      } catch (error) {
        composing.delete(id);
        // Removed: the composer no longer shows it.
        if (controller.signal.aborted) return;
        throw new Error(errorMessage(error));
      } finally {
        inFlight.delete(id);
      }
      yield state({ type: 'requires-action', reason: 'composer-send' });
    },

    async remove(attachment: Attachment) {
      composing.delete(attachment.id);
      // On its way: stopped, and the upload removes what it left on the server.
      inFlight.get(attachment.id)?.controller.abort();
      const file = ready.get(attachment.id);
      ready.delete(attachment.id);
      // Ready and not sent: its row and objects go (a file already sent stays with its message). A delete that fails
      // leaves it to the server's sweep of files no message carries.
      if (file) await discard(file.uploadId);
    },

    dispose() {
      // Only what is still in the composer: a file handed to a message being sent stays with it.
      for (const id of composing) {
        inFlight.get(id)?.controller.abort();
        const file = ready.get(id);
        ready.delete(id);
        if (file) void discard(file.uploadId);
      }
      composing.clear();
    },

    async send(attachment: PendingAttachment): Promise<CompleteAttachment> {
      // Handed to the message: no longer the composer's to count or to drop.
      composing.delete(attachment.id);
      if (attachment.status.type === 'incomplete') {
        throw new Error(`Remove "${attachment.name}" to send: ${attachment.status.message ?? 'it did not upload.'}`);
      }
      // Still on its way: the message waits for it.
      const pending = inFlight.get(attachment.id);
      if (pending) ready.set(attachment.id, await pending.done);
      if (!ready.has(attachment.id)) throw new Error(`"${attachment.name}" did not upload; attach it again.`);
      return { ...attachment, status: { type: 'complete' }, content: [] };
    },
  };
}
